import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { LogIn, AlertCircle, Lock, Mail, ShieldCheck } from 'lucide-react';
import { useAuthStore } from '@/stores/authStore';
import { getSessionExpiredMessage } from '../utils/authSession';
import '@/index.css';

export const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const location = useLocation();
  const { login, isLoading, isAuthenticated, user } = useAuthStore();

  const getDefaultRoute = (role?: string) => (role === 'vendor' ? '/vendor' : '/admin');
  const requestedFrom = (location.state as any)?.from as string | undefined;

  const getRedirectPath = (role?: string) => {
    if (!requestedFrom || requestedFrom === '/login' || requestedFrom === '/') {
      return getDefaultRoute(role);
    }

    if (role === 'vendor' && requestedFrom.startsWith('/admin')) {
      return '/vendor';
    }

    return requestedFrom;
  };

  useEffect(() => {
    if (isAuthenticated && user) {
      const redirectPath = getRedirectPath(user.role);
      navigate(redirectPath, { replace: true });
    }
  }, [isAuthenticated, user, navigate]);

  useEffect(() => {
    const sessionMessage = getSessionExpiredMessage();
    if (sessionMessage) {
      setError(sessionMessage);
    }
  }, []);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!email || !password) {
      setError('Email ve şifre gereklidir');
      return;
    }

    try {
      await login(email, password);
      const authUser = useAuthStore.getState().user;
      const redirectPath = getRedirectPath(authUser?.role);
      navigate(redirectPath, { replace: true });
    } catch (err: any) {
      console.error('Login error:', err);
      const errorMessage = err.message || 'Giriş başarısız';
      
      if (errorMessage.includes('invalid-credential') || errorMessage.includes('wrong-password')) {
        setError('Email veya şifre hatalı');
      } else if (errorMessage.includes('user-not-found')) {
        setError('Kullanıcı bulunamadı');
      } else if (errorMessage.includes('unauthorized')) {
        setError('Bu panel için yetkiniz bulunmamaktadır');
      } else if (errorMessage.includes('too-many-requests')) {
        setError('Çok fazla başarısız deneme. Lütfen daha sonra tekrar deneyin');
      } else if (errorMessage.includes('network')) {
        setError('Bağlantı hatası. İnternet bağlantınızı kontrol edin');
      } else {
        setError(errorMessage);
      }
    }
  };

  return (
    <div className="relative min-h-[100dvh] w-full flex items-center justify-center p-4 bg-slate-950 overflow-hidden font-sans selection:bg-brand-500 selection:text-white">
      {/* Background Glow Mesh Elements */}
      <div className="absolute -top-40 -left-40 w-96 h-96 rounded-full bg-brand-600/30 blur-[120px] pointer-events-none" />
      <div className="absolute -bottom-40 -right-40 w-96 h-96 rounded-full bg-indigo-600/25 blur-[120px] pointer-events-none" />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full bg-purple-900/10 blur-[150px] pointer-events-none" />

      {/* Main Container */}
      <div className="relative w-full max-w-md my-auto">
        {/* Card */}
        <div className="glass-panel rounded-2xl p-6 sm:p-8 shadow-2xl border border-slate-800/80 bg-slate-900/90 text-slate-100">
          {/* Header & Logo */}
          <div className="flex flex-col items-center text-center mb-8">
            <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-tr from-brand-600 to-indigo-500 text-white text-3xl shadow-xl shadow-brand-500/30 mb-3 ring-4 ring-slate-800/60">
              🌹
            </div>
            <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-white">
              TazeBiJest
            </h1>
            <p className="text-xs sm:text-sm font-medium text-slate-400 mt-1">
              Yönetim & Vendor Portalı
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleLogin} className="space-y-5">
            {/* Error Message */}
            {error && (
              <div className="flex items-center gap-3 p-3.5 bg-rose-500/10 border border-rose-500/30 text-rose-300 rounded-xl text-xs sm:text-sm animate-fade-in">
                <AlertCircle size={18} className="flex-shrink-0 text-rose-400" />
                <span>{error}</span>
              </div>
            )}

            {/* Email Input */}
            <div>
              <label className="input-label text-slate-300">E-Posta Adresi</label>
              <div className="relative">
                <Mail size={18} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="admin@tazebijest.com"
                  className="w-full min-h-[46px] pl-10 pr-4 py-2.5 text-sm rounded-xl border border-slate-700 bg-slate-800/80 text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all"
                  required
                />
              </div>
            </div>

            {/* Password Input */}
            <div>
              <div className="flex justify-between items-center mb-1.5">
                <label className="input-label text-slate-300 mb-0">Şifre</label>
              </div>
              <div className="relative">
                <Lock size={18} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="w-full min-h-[46px] pl-10 pr-4 py-2.5 text-sm rounded-xl border border-slate-700 bg-slate-800/80 text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all"
                  required
                />
              </div>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isLoading}
              className="w-full min-h-[46px] mt-2 rounded-xl bg-gradient-to-r from-brand-600 to-indigo-600 hover:from-brand-500 hover:to-indigo-500 text-white font-semibold text-sm sm:text-base shadow-lg shadow-brand-500/25 active:scale-[0.98] transition-all flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {isLoading ? (
                <div className="h-5 w-5 animate-spin rounded-full border-2 border-white border-t-transparent" />
              ) : (
                <>
                  <LogIn size={18} />
                  <span>Giriş Yap</span>
                </>
              )}
            </button>
          </form>

          {/* Footer Security Badge */}
          <div className="mt-6 pt-4 border-t border-slate-800/60 flex items-center justify-center gap-2 text-slate-400 text-xs text-center">
            <ShieldCheck size={14} className="text-emerald-400" />
            <span>Güvenli Yetkili Girişi (Admin / Vendor)</span>
          </div>
        </div>
      </div>
    </div>
  );
};
